import { track } from "@vercel/analytics";

// User consent for non-essential processing (product analytics + error
// telemetry). The app's core function — building/storing trips locally,
// calling the AI to fulfil the user's request — runs regardless, as it's
// necessary to provide the service the user asked for. Only the optional
// observability layer is gated here.

const CONSENT_KEY = "sundayatlas_consent";

export type ConsentChoice = "accepted" | "declined";

export function getConsent(): ConsentChoice | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === "accepted" || v === "declined" ? v : null;
  } catch {
    return null;
  }
}

// Event name broadcast when consent changes, so live consumers (e.g. the
// analytics loader) can react without a page reload.
export const CONSENT_CHANGED_EVENT = "sundayatlas:consent-changed";

export function setConsent(choice: ConsentChoice): void {
  try {
    localStorage.setItem(CONSENT_KEY, choice);
  } catch {
    /* localStorage blocked, choice just won't persist */
  }
  try {
    window.dispatchEvent(
      new CustomEvent(CONSENT_CHANGED_EVENT, { detail: choice })
    );
  } catch {
    /* no window (SSR) or CustomEvent unavailable */
  }
}

export function hasAnalyticsConsent(): boolean {
  return getConsent() === "accepted";
}

// Gated analytics event. No-op unless the user has accepted. Use this
// everywhere instead of importing `track` directly.
export function trackEvent(
  name: string,
  props?: Record<string, string | number | boolean | null>
): void {
  if (hasAnalyticsConsent()) {
    track(name, props);
  }
}
